import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag, ImportWizard } from './ui';
import {
  useC360List, C360Table, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon, CardIcon,
} from './c360';
import { VendorGroupsPage } from './VendorGroupsPage';
import { VendorSubGroupsPage } from './VendorSubGroupsPage';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/vendors';

// status: 1 = Aktif (statusPill semantiği), 0 = Pasif, 2 = Beklemede
const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const GROUP_OPTIONS = [
  { value: 'groupId', label: 'Distribütör Grubu' },
  { value: 'subGroupId', label: 'Ek Grup' },
  { value: 'status', label: 'Durum' },
  { value: 'city', label: 'Şehir' },
];

const GROUP_BY_ODATA = {
  groupId: { field: 'GroupId', type: 'guid' },
  subGroupId: { field: 'SubGroupId', type: 'guid' },
  status: { field: 'Status', type: 'number' },
  city: { field: 'City', type: 'string' },
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  name: { type: 'text', odataField: 'Name' },
  city: { type: 'text', odataField: 'City' },
  groupLabel: { type: 'select', odataField: 'GroupId', valueFormat: 'guid' },
  subGroupLabel: { type: 'select', odataField: 'SubGroupId', valueFormat: 'guid' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};

const SEARCH_FIELDS = ['TextId', 'Name', 'City'];

// Liste/Kanban/Grafik + Kart görünümü. Kart, Liste'nin hemen YANINDA (index 1); geri kalan modlar korunur.
const VENDOR_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const definitionTabs = [
  { key: 'vendors', label: 'Distribütörler' },
  { key: 'groups', label: 'Distribütör Grubu' },
  { key: 'subGroups', label: 'Distribütör Alt Grubu' },
];

// #710: Sekmeler URL'e bağlanır. Grup/Alt Grup kaydı eklenip kaydedildiğinde detay sayfası kendi
// listRoute'una (/vendor-groups) döner; o rota da VendorsPage'i (doğru sekme aktif) render ettiği
// için sekme şeridi kaybolmaz — kullanıcı yine sekmeli ekrana düşer.
const ROUTE_BY_TAB = { vendors: '/vendors', groups: '/vendor-groups', subGroups: '/vendor-sub-groups' };
const TAB_BY_ROUTE = { '/vendors': 'vendors', '/vendor-groups': 'groups', '/vendor-sub-groups': 'subGroups' };

const tabStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

async function readApiError(res, fallback) {
  try {
    const data = await res.json();
    return data?.message || data?.Message || data?.infoMessage || data?.InfoMessage || fallback;
  } catch {
    return fallback;
  }
}

// === Distribütör Grafik görünümü ===
function ChartView({ rows, getGroupLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => Number(r.status) === 1).length;
  const groupSet = new Set(rows.map((r) => r.groupId).filter(Boolean));
  const citySet = new Set(rows.map((r) => r.city).filter(Boolean));
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  const groupCounts = {};
  rows.forEach((r) => {
    const lbl = r.groupId ? getGroupLabel(r.groupId) : 'Atanmamış';
    groupCounts[lbl] = (groupCounts[lbl] || 0) + 1;
  });
  const groupData = Object.entries(groupCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);

  const statusPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const statusCounts = {};
  rows.forEach((r) => {
    const k = r.status == null || r.status === '' ? '__none__' : Number(r.status);
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([k, v]) => {
    const meta = STATUS_META[k];
    return { name: meta?.label || 'Bilinmiyor', value: v, color: meta ? statusPalette[meta.tone] : '#cbd5e1' };
  });

  const cityCounts = {};
  rows.forEach((r) => {
    const lbl = r.city || 'Bilinmiyor';
    cityCounts[lbl] = (cityCounts[lbl] || 0) + 1;
  });
  const cityPalette = ['#0073ea', '#00c875', '#a855f7', '#fdab3d', '#0ea5e9', '#e2445c', '#14b8a6', '#94a3b8'];
  const cityData = Object.entries(cityCounts).map(([name, value], i) => ({ name, value, color: cityPalette[i % cityPalette.length] })).sort((a, b) => b.value - a.value).slice(0, 8);

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Distribütör" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif Distribütör" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Distribütör Grubu" value={groupSet.size.toLocaleString('tr-TR')} hint="farklı grup" />
        <KpiCard label="Şehir" value={citySet.size.toLocaleString('tr-TR')} hint="farklı şehir" />
        <KpiCard label="Bu Ay Eklenen" value={newThisMonth.toLocaleString('tr-TR')} hint="Yeni distribütör" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Distribütör Grubuna Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {groupData.length} grup</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={groupData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} distribütör`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} distribütör`} data={statusData} />
        <DonutCard title="Şehre Göre" subtitle={`İlk ${cityData.length}`} data={cityData} />
      </div>
    </div>
  );
}

export function VendorsPage() {
  usePageMeta('Distribütörler', 'Distribütör kayıtlarını yönetin');
  const navigate = useNavigate();
  const location = useLocation();
  // #710: aktif sekme URL'den türetilir (yerel state değil) → hangi rotaya düşülürse düşülsün
  // (özellikle detay kaydından listRoute'a dönüşte) doğru sekme aktif kalır.
  const activeDefinition = TAB_BY_ROUTE[location.pathname] || 'vendors';
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  // $select: düz liste görünümünde yalnız GÖRÜNÜR kolonların OData kaynak alanlarını çekeriz
  // (Column Chooser ile entegre). `always` = 'Id' (satır anahtarı + /vendors/:id nav) + 'Name'
  // (silme onay diyaloğu selected.name okur; Adı kolonu gizlense de gerekir).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'Name'],
  }), [columnVisibility]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getGroupLabel = useCallback((id) => groups.find((item) => item.id === id)?.description || groups.find((item) => item.id === id)?.textId || '-', [groups]);
  const getSubGroupLabel = useCallback((id) => subGroups.find((item) => item.id === id)?.description || subGroups.find((item) => item.id === id)?.textId || '-', [subGroups]);

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendorgroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/vendorsubgroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = field === 'status' ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
    else if (field === 'groupId') label = getGroupLabel(key);
    else if (field === 'subGroupId') label = getSubGroupLabel(key);
    else label = String(key);
    return { key, label };
  }, [getGroupLabel, getSubGroupLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'status') return Object.entries(STATUS_META).map(([k, meta]) => ({ key: Number(k), label: meta.label }));
    if (groupField === 'groupId') return groups.map((g) => ({ key: g.id, label: getGroupLabel(g.id) }));
    if (groupField === 'subGroupId') return subGroups.map((g) => ({ key: g.id, label: getSubGroupLabel(g.id) }));
    return [];
  }, [groups, subGroups, getGroupLabel, getSubGroupLabel]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    defaultKanbanGroup: 'status',
    getKeyLabel,
    seedKanbanBuckets,
    selectFields,
    ready: lookupsLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  // Ana kayıt new/edit/view artık tam-sayfa /vendors/:id detayında (VendorDetailPage).
  const openNew = () => navigate('/vendors/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/vendors/${row.id}`, { state: { row } }); };
  // #282 kalem → düzenleme modu (DetailShell ?edit=1 ile açılır).
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/vendors/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Distribütör başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Distribütör Kodu', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', {
      header: 'Adı', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        return <span className="c360-customer-cell"><AvatarBadge name={v} tone="customer" /><span className="c360-cell-main">{v}</span></span>;
      },
    }),
    columnHelper.accessor('city', { header: 'Şehir', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor((row) => getGroupLabel(row.groupId), {
      id: 'groupLabel', header: 'Distribütör Grubu', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor((row) => getSubGroupLabel(row.subGroupId), {
      id: 'subGroupLabel', header: 'Ek Grup', enableSorting: false, meta: { filterType: 'select' },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined || v === '') return '-';
        const meta = STATUS_META[v] ?? { label: String(v), tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getGroupLabel, getSubGroupLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    groupLabel: groups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    subGroupLabel: subGroups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
  }), [groups, subGroups]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options || lookupOptions[columnId] }), [lookupOptions]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Distribütör Kodu' },
        { field: 'name', headerName: 'Adı' },
        { field: 'city', headerName: 'Şehir' },
        { field: 'erpId', headerName: 'ERP Kodu' },
        { headerName: 'Distribütör Grubu', valueGetter: ({ row }) => getGroupLabel(row.groupId) },
        { headerName: 'Ek Grup', valueGetter: ({ row }) => getSubGroupLabel(row.subGroupId) },
        { field: 'region1', headerName: 'Bölge 1' },
        { field: 'region2', headerName: 'Bölge 2' },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
        { field: 'vendorCurrency', headerName: 'Para Birimi' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `tedarikciler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const importColumnKeys = {
    // #819 Görünen ad "Distribütör" oldu; eski başlıklı dosyalar da içe aktarılabilsin diye
    // legacy takma adlar (Dağıtım Kanalı / Tedarikçi) listede korunur.
    textId: ['Distribütör Kodu', 'Distributor Kodu', 'Dağıtım Kanalı Kodu', 'Tedarikçi Kodu', 'Tedarikci Kodu', 'textId', 'TextId', 'Kod'],
    name: ['Adı', 'Adi', 'Ad', 'name', 'Name'],
    city: ['Şehir', 'Sehir', 'city', 'City'],
    erpId: ['ERP Kodu', 'erpId', 'ErpId'],
  };
  const importRowToPayload = (row) => {
    const pick = (keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
    const out = {};
    for (const [field, keys] of Object.entries(importColumnKeys)) out[field] = pick(keys);
    return out;
  };
  const importValidateRow = (p) => {
    if (!p.textId) return 'Distribütör Kodu boş';
    if (!p.name) return 'Ad boş';
    return null;
  };
  const importSubmitRow = async (p) => {
    const payload = {
      textId: p.textId, name: p.name, city: p.city || null, erpId: p.erpId || null,
      groupId: null, subGroupId: null, region1: null, region2: null, status: 1,
      vendorCurrency: null, customerRelationId: null, vendorCurrencyTypeDeleted20251118071733: null,
    };
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    const sample = [{ 'Distribütör Kodu': 'V001', 'Adı': 'Örnek Distribütör A.Ş.', 'Şehir': 'İstanbul', 'ERP Kodu': 'ERP001' }];
    exportToExcel(sample, null, 'tedarikci_sablon');
  };

  const renderTabs = () => (
    <div style={tabStyles.tabBar}>
      {definitionTabs.map((tab) => (
        <button key={tab.key} type="button" style={{ ...tabStyles.tab, ...(activeDefinition === tab.key ? tabStyles.tabActive : {}) }} onClick={() => navigate(ROUTE_BY_TAB[tab.key])}>{tab.label}</button>
      ))}
    </div>
  );

  if (activeDefinition === 'groups') return <div style={s.container}>{renderTabs()}<VendorGroupsPage /></div>;
  if (activeDefinition === 'subGroups') return <div style={s.container}>{renderTabs()}<VendorSubGroupsPage /></div>;

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, ad veya şehir ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      {renderTabs()}
      <ListPageShell
        title="Distribütörler"
        subtitle="Distribütör kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={VENDOR_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Distribütör</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getGroupLabel={getGroupLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const rowStatusMeta = STATUS_META[row.status];
                        const isV = (id) => columnVisibility[id] !== false;
                        const groupLbl = getGroupLabel(row.groupId);
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openDetail(row)}>
                            {isV('name') && (
                              <div className="c360-kanban-card-head">
                                {row.name && <AvatarBadge name={row.name} tone="customer" size={28} />}
                                <span className="c360-kanban-card-title" title={row.name || ''}>{row.name || '-'}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              {isV('textId') && row.textId && <span className="c360-erp-chip">{row.textId}</span>}
                              {isV('status') && rowStatusMeta && <StatusBadge tone={rowStatusMeta.tone}>{rowStatusMeta.label}</StatusBadge>}
                              {isV('groupLabel') && groupLbl && groupLbl !== '-' && <span className="c360-erp-chip" title="Distribütör Grubu">{groupLbl}</span>}
                              {isV('city') && row.city && <span className="c360-erp-chip" title="Şehir">{row.city}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const meta = STATUS_META[row.status];
              const grp = getGroupLabel(row.groupId);
              return {
                avatarName: row.name,
                avatarTone: 'customer',
                title: row.name,
                subtitle: row.textId,
                statusTone: meta?.tone,
                statusLabel: meta?.label,
                meta: [
                  row.city ? { k: 'Şehir', v: row.city } : null,
                  grp && grp !== '-' ? { k: 'Grup', v: grp } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openDetail },
              { kind: 'edit', title: 'Değiştir', onClick: openEditRow, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> distribütörsini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} distribütör içe aktarıldı`); fetchData(); } }}
        title="Distribütör İçe Aktar"
        helpText="Şablondaki kolon başlıklarını koruyun. Zorunlu alanlar: Distribütör Kodu ve Adı."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
